import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Zap } from 'lucide-react';
import { logoAltUrl } from '../data/mock';
import { blogPosts } from '../data/blogPosts';
import { useSEO } from '../hooks/useSEO';

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useSEO(
    post ? `${post.title} | Nexline Electrical Ltd.` : 'Article Not Found | Nexline Electrical Ltd.',
    post ? post.excerpt : undefined
  );

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5">
        <div className="max-w-[800px] mx-auto px-6 h-24 flex items-center justify-between">
          <Link to="/" data-testid="blog-post-logo-home-link" className="flex items-center gap-3">
            <img src={logoAltUrl} alt="Nexline Electrical" className="h-14 w-auto object-contain" />
          </Link>
          <Link
            to="/blog"
            data-testid="blog-post-back-to-blog-link"
            className="flex items-center gap-2 text-neutral-400 hover:text-electric-yellow text-sm font-medium transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
        </div>
      </header>

      <main className="max-w-[800px] mx-auto px-6 py-16 lg:py-24" data-testid="blog-post-page">
        <span className="text-electric-yellow text-sm font-semibold uppercase tracking-wider flex items-center gap-2 mb-4">
          <Zap size={14} />
          {post.category}
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
          {post.title}
        </h1>
        <div className="flex items-center gap-3 text-neutral-500 text-sm mb-10">
          <span>{post.author}</span>
          <span>&middot;</span>
          <span>{formatDate(post.date)}</span>
        </div>

        <div className="aspect-[16/9] overflow-hidden mb-12 border border-white/10">
          <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <article className="space-y-6">
          {post.content.map((paragraph, idx) => (
            <p key={idx} className="text-neutral-300 leading-relaxed text-lg">
              {paragraph}
            </p>
          ))}
        </article>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row gap-4">
          <Link
            to="/blog"
            data-testid="blog-post-back-to-blog-button"
            className="inline-flex items-center gap-2 bg-electric-yellow text-black px-6 py-3 text-base font-medium hover:bg-transparent hover:text-electric-yellow border border-electric-yellow transition-all duration-400"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
          <Link
            to="/#contact"
            data-testid="blog-post-get-quote-link"
            className="inline-flex items-center gap-2 bg-white/5 text-white px-6 py-3 text-base font-medium hover:bg-white hover:text-black border border-white/20 transition-all duration-400"
          >
            Request a Free Quote
          </Link>
        </div>

        {otherPosts.length > 0 && (
          <div className="mt-16 pt-8 border-t border-white/5">
            <h3 className="text-xl font-semibold text-white mb-6">More Articles</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {otherPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-blog-card-${p.slug}`}
                  className="group block bg-neutral-950 border border-white/5 hover:border-electric-yellow/40 transition-all duration-300 p-5"
                >
                  <span className="text-electric-yellow text-xs font-semibold uppercase tracking-wider">
                    {p.category}
                  </span>
                  <h4 className="text-white font-medium mt-2 group-hover:text-electric-yellow transition-colors duration-300">
                    {p.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BlogPost;
